import { StatusThemeVariant } from '@/classes/SubmissionStatuses/StatusThemeVariantEnum'
import { type SubmissionAction } from '@/classes/SubmissionStatuses/types'
import type { SubmissionStatusLineProp } from '@/components/home/SubmissionStatuses/SubmissionStatusLine.vue'
import type { SubmissionChannel } from '@/services/types'
import type { UploadItem } from '@/types'

export type LocalSubmissionStatus = {
	submissionChannel: SubmissionChannel
	status: UploadItem<any>['uploadStatus']
}

export abstract class SubmissionStatusMapper {
	protected localSubmissionStatus: LocalSubmissionStatus[]

	constructor(localSubmissionStatus: LocalSubmissionStatus[] = []) {
		this.localSubmissionStatus = localSubmissionStatus
	}

	protected getFailedOutgoingChannels(
		channels: SubmissionChannel[]
	): SubmissionChannel[] {
		return this.localSubmissionStatus
			.filter(
				({ submissionChannel, status }) =>
					channels.includes(submissionChannel) &&
					['sending', 'failed', 'waitingForNetwork'].includes(status)
			)
			.map(({ submissionChannel }) => submissionChannel)
	}

	/** returns submission vendor name like OAP, NGL, Everplans */
	abstract getVendorName(): string | undefined

	/** returns the style variant -> success, warning, error, neutral */
	abstract getThemeVariant(): StatusThemeVariant

	/** This might just convert the status to Title case, or maybe changes it more if it's missing email or something */
	abstract getStatusText(): string

	/** This returns instructional text for inside the tooltip for this submission status line, if said text exists */
	abstract getToolTipText(): { title: string; body: string }

	/** This will return the button action, such as add email, or upgrade */
	abstract getActions(): Array<SubmissionAction>

	abstract getUpdatedAt(): string

	getLineProps(): SubmissionStatusLineProp {
		return {
			variant: this.getThemeVariant(),
			statusText: this.getStatusText(),
			timeStamp: this.getUpdatedAt(),
			actions: this.getActions(),
			toolTipText: this.getToolTipText(),
		}
	}
}

export const isSubmissionError = (
	submission?: SubmissionStatusMapper
): boolean => {
	return submission?.getThemeVariant() === StatusThemeVariant.ERROR
}
